// src/features/collaboration/components/EventCreationModal.js
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, FormControlLabel, Switch, Stack, IconButton, InputLabel,
  FormControl, Select, MenuItem
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { createEvent, repostEventToFeed } from '../services/collaborationService';
import { useAuth } from '../../../contexts/AuthContext';

const EventCreationModal = ({ open, onClose, onCreated }) => {
  const { currentUser } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [eventType, setEventType] = useState('in_person');
  const [venue, setVenue] = useState('');
  const [virtualPlatform, setVirtualPlatform] = useState('');
  const [virtualLink, setVirtualLink] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [bannerFile, setBannerFile] = useState(null);
  const [postToFeed, setPostToFeed] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setTitle(''); setDescription(''); setDate(''); setTime('');
    setEventType('in_person'); setVenue(''); setVirtualPlatform(''); setVirtualLink('');
    setIsPublic(true); setBannerFile(null); setPostToFeed(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setBannerFile(file);
  };

  const handleSubmit = async () => {
    if (!currentUser) return;
    setSubmitting(true);
    try {
      const location = eventType === 'virtual' ? virtualPlatform || 'Virtual' : venue;
      const eventData = {
        title,
        description,
        date: date ? new Date(`${date}T${time || '00:00'}`).toISOString() : null,
        time,
        location,
        eventType,
        venue: eventType === 'in_person' ? venue : '',
        virtualPlatform: eventType === 'virtual' ? virtualPlatform : '',
        virtualLink: eventType === 'virtual' ? virtualLink : '',
        isPublic
      };

      const newEvent = await createEvent(currentUser.uid, eventData, bannerFile);

      // Optionally repost to feed
      if (postToFeed) {
        await repostEventToFeed(currentUser.uid, { id: newEvent.id, ...newEvent });
      }

      onCreated && onCreated(newEvent);
      reset();
      onClose();
    } catch (error) {
      console.error('Error creating event', error);
      // TODO: show snackbar / toast if you have one
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create Event</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth required />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={3} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Date" type="date" InputLabelProps={{ shrink: true }} value={date} onChange={(e) => setDate(e.target.value)} />
            <TextField label="Time" type="time" InputLabelProps={{ shrink: true }} value={time} onChange={(e) => setTime(e.target.value)} />
          </Box>
          <FormControl fullWidth>
            <InputLabel id="event-type-label">Event Type</InputLabel>
            <Select
              labelId="event-type-label"
              value={eventType}
              label="Event Type"
              onChange={(e) => setEventType(e.target.value)}
            >
              <MenuItem value="in_person">In-person</MenuItem>
              <MenuItem value="virtual">Virtual</MenuItem>
            </Select>
          </FormControl>
          {eventType === 'in_person' ? (
            <TextField
              label="Venue / Address"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              fullWidth
            />
          ) : (
            <Stack spacing={2}>
              <TextField
                label="Virtual Platform"
                placeholder="e.g., Zoom, Google Meet, Microsoft Teams"
                value={virtualPlatform}
                onChange={(e) => setVirtualPlatform(e.target.value)}
                fullWidth
              />
              <TextField
                label="Virtual Link (optional)"
                value={virtualLink}
                onChange={(e) => setVirtualLink(e.target.value)}
                fullWidth
              />
            </Stack>
          )}
          <FormControlLabel control={<Switch checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />} label="Public event" />
          <Box>
            <InputLabel sx={{ mb: 1 }}>Banner image (optional)</InputLabel>
            <label htmlFor="event-banner-file">
              <input accept="image/*" id="event-banner-file" type="file" style={{ display: 'none' }} onChange={handleFileChange} />
              <Button variant="outlined" component="span" startIcon={<PhotoCamera />}>
                Upload image
              </Button>
              {bannerFile && <Box component="span" sx={{ ml: 2 }}>{bannerFile.name}</Box>}
            </label>
          </Box>
          <FormControlLabel control={<Switch checked={postToFeed} onChange={(e) => setPostToFeed(e.target.checked)} />} label="Post to feed" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || !title || !date}>
          {submitting ? 'Creating…' : 'Create Event'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventCreationModal;
